import { useState } from 'react';
import {
  useEditWorkAssignment,
  WorkAssignment,
} from '../../../hooks/workAssignmentHooks';
import PratusTextArea from 'components/DesignSystems/New/PratusTextArea';
import PratusInput from 'components/DesignSystems/New/PratusInput';
import StylishDateTimePicker from 'components/DesignSystems/form/StylishDateTimePicker';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import classNames from 'classnames';
import PratusSelect, {
  OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import { WorkAssignmentResources } from './WorkAssignmentResources';
import { useCurrentPeriod } from 'components/IAP/hooks/incidentHooks';

export const WorkAssignmentItem: React.FC<{
  work: WorkAssignment;
  workGroups: { id: string; name: string }[];
  resourceOptions: { label: string; value: string }[];
  readOnly?: boolean;
}> = ({ work, workGroups, resourceOptions, readOnly = false }) => {
  const [expanded, setExpanded] = useState(false);
  const editWorkAssignment = useEditWorkAssignment(work.work_assignment_id);
  const [editing, setEditing] = useState(false);

  const operationalPeriod = useCurrentPeriod();

  const periodNumber = operationalPeriod?.currentPeriod?.period_number || 0;

  const handleEdit = async (field: string, value: string) => {
    editWorkAssignment.mutateAsync({
      ...work,
      [field]: value,
      operational_period_number: periodNumber + 1,
    });
  };

  const handleDelete = () => {
    editWorkAssignment.mutate({ ...work, archived: true });
  };

  const handleDateChange = (e: Date) => {
    editWorkAssignment.mutate({
      ...work,
      requested_arrival_time: e,
      operational_period_number: periodNumber + 1,
    });
  };

  const handleTimeZoneChange = (value: number) => {
    editWorkAssignment.mutate({
      ...work,
      utc_offset: value,
      operational_period_number: periodNumber + 1,
    });
  };

  const handleSave = (newResource: { label: string; value: string }) => {
    editWorkAssignment.mutateAsync({
      ...work,
      resource_list: [...(work.resource_list || []), newResource],
      operational_period_number: periodNumber + 1,
    });
  };

  const handleRemoveItem = (index: number) => {
    const updatedResources =
      work.resource_list?.filter((_: any, i: number) => i !== index) || [];
    editWorkAssignment.mutateAsync({
      ...work,
      resource_list: updatedResources,
      operational_period_number: periodNumber + 1,
    });
  };

  return (
    <>
      <div
        className={classNames('pratus-collapsible-item', {
          'align-items-start': editing,
          corner: expanded,
        })}
        role="button"
        onClick={() => !editing && setExpanded(!expanded)}
      >
        {!editing ? (
          <div>
            <div>{work.work_assignment}</div>
            <div>Work Group: {work.work_group_name}</div>
          </div>
        ) : (
          <div className="flex-grow-1">
            <PratusInput
              placeholder="Enter work assignment name"
              value={work.work_assignment}
              onSave={async (e) => {
                await editWorkAssignment.mutateAsync({
                  ...work,
                  work_assignment: e,
                });
                setEditing(false);
              }}
              showErrorContainer={false}
              maxLength={255}
              onCancel={() => setEditing(false)}
            />
            <PratusSelect
              label="Work Group"
              options={workGroups.map((group) => ({
                label: group.name,
                value: group.id,
              }))}
              value={{
                label: work.work_group_name || '',
                value: work.work_group_id || '',
              }}
              onChange={(newValue) => {
                const option = newValue as OptionType | null;
                const id = (option?.value as string) ?? '';
                const label = (option?.label as string) ?? '';
                editWorkAssignment.mutateAsync({
                  ...work,
                  work_group_id: id,
                  work_group_name: label,
                });
                setEditing(false);
              }}
            />
          </div>
        )}
        {!readOnly && (
          <div>
            <StylishNewButton
              className="button--link"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(!editing);
              }}
            >
              <SharedIcon iconName="edit" color="light" />
            </StylishNewButton>
            <StylishNewButton
              className="button--link"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
            >
              <SharedIcon iconName="delete" color="light" />
            </StylishNewButton>
          </div>
        )}
      </div>
      {expanded && (
        <div
          className="pratus-collapsible-item__content"
          style={{ backgroundColor: '#222529' }}
        >
          <WorkAssignmentResources
            resourceOptions={resourceOptions}
            resourceList={work.resource_list}
            handleSave={handleSave}
            handleRemove={handleRemoveItem}
            readOnly={readOnly}
          />
          <PratusTextArea
            label="Overhead Position"
            value={work.overhead_positions}
            onSave={(value: string) => handleEdit('overhead_positions', value)}
            disabled={readOnly}
          />
          <PratusTextArea
            label="Special Instructions"
            value={work.special_instructions}
            onSave={(value: string) =>
              handleEdit('special_instructions', value)
            }
            disabled={readOnly}
          />
          <PratusTextArea
            label="Reporting Location"
            value={work.reporting_location}
            onSave={(value: string) => handleEdit('reporting_location', value)}
            disabled={readOnly}
          />
          <StylishDateTimePicker
            value={work.requested_arrival_time || null}
            onChange={(e) => e && handleDateChange(new Date(e))}
            type="datetime-local"
            timeZone
            timeZoneLabel
            timeZoneValue={work.utc_offset}
            onTimeZoneChange={(e) => handleTimeZoneChange(e.value)}
            disabled={readOnly}
          />
        </div>
      )}
    </>
  );
};
